import { ReactNode } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import UFOSegment from "@atlaskit/react-ufo/segment";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";
import { useLocalizedHref } from "@/libraries/web-framework";

import {
    CollectionStrategyLockup,
    CollectionTeamworkLockup,
    IconAgentsStrategy,
    IconAgentsTeamwork,
    IconAlign,
    IconConfluence,
    IconFocus,
    IconJira,
    IconLoom,
    IconTalent
} from "./collection-lockups";

const AppWrap = ({ children, name }: { children: ReactNode; name: string }) => {
    return (
        <div css={AppWrapCSS}>
            {children}
            <h6 className="app-wrap-name" css={AppWrapName}>
                {name}
            </h6>
        </div>
    );
};

export const CollectionTeamwork = () => {
    const intl = useIntl();

    const CollectionTeamworkTagline = intl.formatMessage({
        id: "GlobalNav.collections.teamwork.tagline",
        defaultMessage: "Apps and agents to supercharge teamwork"
    });
    return (
        <UFOSegment name="collections-box-teamwork">
            <a href={useLocalizedHref("/collections/teamwork")}>
                <div css={CollectionBoxStyle}>
                    <CollectionTeamworkLockup />
                    <p className="collections-tagline" css={CollectionP}>
                        {CollectionTeamworkTagline}
                    </p>
                    <AppWrap name="Jira">
                        <IconJira />
                    </AppWrap>
                    <AppWrap name="Confluence">
                        <IconConfluence />
                    </AppWrap>
                    <AppWrap name="Loom">
                        <IconLoom />
                    </AppWrap>
                    <AppWrap name="Agents">
                        <IconAgentsTeamwork />
                    </AppWrap>
                </div>
            </a>
        </UFOSegment>
    );
};

export const CollectionStrategy = () => {
    const intl = useIntl();

    const CollectionStrategyTagline = intl.formatMessage({
        id: "GlobalNav.collections.strategy.tagline",
        defaultMessage: "Powerful apps to optimize strategy"
    });
    return (
        <UFOSegment name="collections-box-strategy">
            <a
                css={CollectionHrefBlock}
                href={useLocalizedHref("/collections/strategy")}
            >
                <div css={CollectionBoxStyle}>
                    <CollectionStrategyLockup />
                    <p className="collections-tagline" css={CollectionP}>
                        {CollectionStrategyTagline}
                    </p>

                    <AppWrap name="Align">
                        <IconAlign />
                    </AppWrap>
                    <AppWrap name="Focus">
                        <IconFocus />
                    </AppWrap>
                    <AppWrap name="Talent">
                        <IconTalent />
                    </AppWrap>
                    <AppWrap name="Agents">
                        <IconAgentsStrategy />
                    </AppWrap>
                </div>
            </a>
        </UFOSegment>
    );
};

const CollectionBoxStyle = css({
    background: wacToken("color.neutral.light100"),
    padding: token("space.250"),
    position: "relative",
    borderRadius: wacToken("corner.radius.card"),
    transition: "box-shadow 0.25s",
    display: "flex",
    flexWrap: "wrap",
    height: "100%",
    width: "296px",
    maxWidth: "330px",
    [`:hover`]: {
        // eslint-disable-next-line local/ensure-wac-token-usage
        boxShadow: "0px 8px 12px 0px #091E4226, 0px 0px 1px 0px #091E424F"
    },
    [media.below.md]: {
        width: "auto"
    }
});

const CollectionHrefBlock = css({
    display: "block"
});

const CollectionP = css({
    [`&.collections-tagline`]: {
        fontSize: "0.75rem",
        margin: `${token("space.025")} 0 ${token("space.150")}`,
        lineHeight: 1.25,
        width: "100%"
    }
});

const AppWrapCSS = css({
    width: "50%",
    display: "flex",
    alignItems: "center",
    gap: token("space.050"),
    marginBlockEnd: token("space.075")
});

const AppWrapName = css({
    [`&.app-wrap-name`]: {
        lineHeight: 1,
        fontSize: "0.875rem",
        margin: 0
    }
});
